import {createPerformanceDebugTrace, type PerformanceDebugData} from "@pg-mono/performance-debug";

import {loadTurnstileScript} from "./load_turnstile_script";
import {createDefaultTurnstileChallengeContainer, CreateTurnstileChallengeContainer, TurnstileWidgetTheme} from "./turnstile_challenge_container";
import {createTurnstileValidationError} from "./turnstile_error";
import {
    getTurnstileSimulationMode,
    TURNSTILE_FORCE_CHALLENGE_SITE_KEY,
    TURNSTILE_SIMULATION_CHALLENGE_MODE,
    TURNSTILE_SIMULATION_FAIL_MODE
} from "./turnstile_simulation";

type TurnstileWidgetId = string;

type TurnstileRenderOptions = {
    sitekey: string;
    action: string;
    appearance: "always" | "interaction-only";
    execution: "execute";
    size: "flexible";
    theme: TurnstileWidgetTheme;
    callback: (token: string) => void;
    "before-interactive-callback": () => void;
    "after-interactive-callback": () => void;
    "error-callback": () => void;
    "expired-callback": () => void;
    "timeout-callback": () => void;
    "unsupported-callback": () => void;
};

declare global {
    interface Window {
        turnstile?: {
            render: (container: HTMLElement | string, options: TurnstileRenderOptions) => TurnstileWidgetId;
            execute: (widgetId: TurnstileWidgetId) => void;
            remove: (widgetId: TurnstileWidgetId) => void;
        };
    }
}

const TURNSTILE_TIMEOUT_MS = 30000;
const TURNSTILE_PREFETCH_TOKEN_MAX_AGE_MS = 240000;
const CLOUDFLARE_TURNSTILE_PERFORMANCE_GROUP = "cloudflare_turnstile";

interface IPrefetchedTurnstileToken {
    isResolved?: boolean;
    promise?: Promise<string | undefined>;
    resolvedAt?: number;
    token?: string;
}

type PrefetchedTokenResult = {status: "hit"; token?: string} | {status: "miss"};

const prefetchedTurnstileTokens = new Map<string, IPrefetchedTurnstileToken>();

interface IGetTurnstileTokenOptions {
    siteKey: string | null | undefined;
    action: string;
    createChallengeContainer?: CreateTurnstileChallengeContainer;
    simulationStorageKey?: string;
}

interface ICreateTurnstileOptions {
    siteKey: string | null | undefined;
    createChallengeContainer?: CreateTurnstileChallengeContainer;
    simulationStorageKey?: string;
}

const getTurnstileCacheKey = (options: IGetTurnstileTokenOptions) => {
    const simulationMode = getTurnstileSimulationMode(options.simulationStorageKey);
    const siteKey = getSiteKey({configuredSiteKey: options.siteKey, simulationMode});

    return `${siteKey ?? "disabled"}:${options.action}:${simulationMode ?? "off"}`;
};

const getSiteKey = ({
    configuredSiteKey,
    simulationMode
}: {
    configuredSiteKey: string | null | undefined;
    simulationMode: ReturnType<typeof getTurnstileSimulationMode>;
}) => {
    return simulationMode === null ? configuredSiteKey : TURNSTILE_FORCE_CHALLENGE_SITE_KEY;
};

const isPrefetchedTokenFresh = (entry?: IPrefetchedTurnstileToken) => {
    return Boolean(entry?.isResolved && entry.resolvedAt && Date.now() - entry.resolvedAt < TURNSTILE_PREFETCH_TOKEN_MAX_AGE_MS);
};

const createTurnstilePerformanceDebugTrace = (flow: string, initialData?: PerformanceDebugData) =>
    createPerformanceDebugTrace(flow, initialData, {group: CLOUDFLARE_TURNSTILE_PERFORMANCE_GROUP});

const consumePrefetchedTurnstileToken = async (
    options: IGetTurnstileTokenOptions,
    trace: ReturnType<typeof createPerformanceDebugTrace>
): Promise<PrefetchedTokenResult> => {
    const cacheKey = getTurnstileCacheKey(options);
    const cachedEntry = prefetchedTurnstileTokens.get(cacheKey);

    if (!cachedEntry) {
        trace.mark("turnstile_prefetch_cache:miss");
        return {status: "miss"};
    }

    prefetchedTurnstileTokens.delete(cacheKey);

    if (isPrefetchedTokenFresh(cachedEntry)) {
        trace.mark("turnstile_prefetch_cache:hit", {state: "resolved"});
        return {status: "hit", token: cachedEntry.token};
    }

    if (cachedEntry.promise) {
        trace.mark("turnstile_prefetch_cache:hit", {state: "pending"});
        const token = await cachedEntry.promise;
        trace.mark("turnstile_prefetch_cache:pending_resolved", {hasToken: Boolean(token)});

        return cachedEntry.isResolved ? {status: "hit", token} : {status: "miss"};
    }

    trace.mark("turnstile_prefetch_cache:expired");
    return {status: "miss"};
};

export const getTurnstileToken = async (options: IGetTurnstileTokenOptions): Promise<string | undefined> => {
    const {siteKey: configuredSiteKey, action, createChallengeContainer = createDefaultTurnstileChallengeContainer, simulationStorageKey} = options;

    const simulationMode = getTurnstileSimulationMode(simulationStorageKey);
    const siteKey = getSiteKey({configuredSiteKey, simulationMode});
    const trace = createTurnstilePerformanceDebugTrace("turnstile_token", {
        action,
        hasConfiguredSiteKey: Boolean(configuredSiteKey),
        isBrowser: typeof window !== "undefined",
        simulationMode: simulationMode ?? "off"
    });

    if (typeof window === "undefined" || !siteKey) {
        trace.end("turnstile_token:skipped", {
            reason: typeof window === "undefined" ? "not_browser" : "missing_site_key"
        });
        return undefined;
    }

    try {
        trace.mark("turnstile_script:load_start");
        await loadTurnstileScript(trace);
        trace.mark("turnstile_script:ready", {hasTurnstileApi: Boolean(window.turnstile)});

        if (!window.turnstile) {
            throw new Error("Turnstile is not available");
        }

        const token = await new Promise<string>((resolve, reject) => {
            const challengeContainer = createChallengeContainer({
                onDismiss: () => {
                    trace.mark("turnstile_challenge:dismissed");
                    rejectOnce("dismissed");
                }
            });

            let widgetId: TurnstileWidgetId | null = null;
            let isSettled = false;

            const cleanup = () => {
                window.clearTimeout(timeoutId);

                if (widgetId && window.turnstile) {
                    window.turnstile.remove(widgetId);
                    trace.mark("turnstile_widget:removed", {widgetId});
                }

                challengeContainer.remove();
                trace.mark("turnstile_challenge:container_removed");
            };

            const resolveOnce = (token: string) => {
                if (isSettled) {
                    return;
                }

                isSettled = true;
                trace.mark("turnstile_token:callback_success", {hasToken: Boolean(token)});
                cleanup();
                resolve(token);
            };

            const rejectOnce = (reason = "validation_error") => {
                if (isSettled) {
                    return;
                }

                isSettled = true;
                trace.mark("turnstile_token:rejected", {reason});
                cleanup();
                reject(createTurnstileValidationError());
            };

            const timeoutId = window.setTimeout(() => rejectOnce("local_timeout"), TURNSTILE_TIMEOUT_MS);

            if (!window.turnstile) {
                rejectOnce("missing_window_api");
                return;
            }

            if (simulationMode === TURNSTILE_SIMULATION_CHALLENGE_MODE) {
                trace.mark("turnstile_challenge:show_for_simulation");
                challengeContainer.show();
            }

            trace.mark("turnstile_widget:render_start");
            widgetId = window.turnstile.render(challengeContainer.widgetContainer, {
                sitekey: siteKey,
                action,
                appearance: simulationMode === TURNSTILE_SIMULATION_CHALLENGE_MODE ? "always" : "interaction-only",
                execution: "execute",
                size: "flexible",
                theme: challengeContainer.widgetTheme ?? "auto",
                callback: resolveOnce,
                "before-interactive-callback": () => {
                    trace.mark("turnstile_challenge:before_interactive");
                    challengeContainer.show();
                },
                "after-interactive-callback": () => trace.mark("turnstile_challenge:after_interactive"),
                "error-callback": () => rejectOnce("widget_error"),
                "expired-callback": () => rejectOnce("expired"),
                "timeout-callback": () => rejectOnce("widget_timeout"),
                "unsupported-callback": () => rejectOnce("unsupported")
            });

            trace.mark("turnstile_widget:render_done", {widgetId});
            window.turnstile.execute(widgetId);
            trace.mark("turnstile_widget:execute_done", {widgetId});
        });

        if (simulationMode === TURNSTILE_SIMULATION_FAIL_MODE) {
            trace.end("turnstile_token:simulation_failed");
            throw createTurnstileValidationError();
        }

        if (simulationMode === TURNSTILE_SIMULATION_CHALLENGE_MODE) {
            trace.end("turnstile_token:simulation_challenge_resolved");
            return undefined;
        }

        trace.end("turnstile_token:resolved", {hasToken: Boolean(token)});
        return token;
    } catch (error) {
        trace.end("turnstile_token:failed", {
            isAppError: typeof error === "object" && error !== null && "appError" in error
        });

        if (typeof error === "object" && error !== null && "appError" in error) {
            throw error;
        }

        throw createTurnstileValidationError();
    }
};

export const prefetchTurnstileToken = (options: IGetTurnstileTokenOptions): void => {
    const cacheKey = getTurnstileCacheKey(options);
    const trace = createTurnstilePerformanceDebugTrace("turnstile_prefetch_token", {
        action: options.action
    });
    const cachedEntry = prefetchedTurnstileTokens.get(cacheKey);

    if (cachedEntry) {
        if (cachedEntry.promise || isPrefetchedTokenFresh(cachedEntry)) {
            trace.end("turnstile_prefetch:skipped", {reason: cachedEntry.promise ? "pending" : "fresh_token"});
            return;
        }
    }

    const nextEntry: IPrefetchedTurnstileToken = {};
    const promise = getTurnstileToken(options)
        .then((token) => {
            nextEntry.isResolved = true;
            nextEntry.promise = undefined;
            nextEntry.resolvedAt = Date.now();
            nextEntry.token = token;
            trace.end("turnstile_prefetch:done", {hasToken: Boolean(token)});

            return token;
        })
        .catch(() => {
            prefetchedTurnstileTokens.delete(cacheKey);
            trace.end("turnstile_prefetch:error");

            return undefined;
        });

    nextEntry.promise = promise;
    prefetchedTurnstileTokens.set(cacheKey, nextEntry);
};

export const appendTurnstileToken = async <T extends object>(
    payload: T,
    options: IGetTurnstileTokenOptions
): Promise<T & {turnstile_token?: string; action: string}> => {
    const trace = createTurnstilePerformanceDebugTrace("turnstile_append_payload", {
        action: options.action
    });
    trace.mark("turnstile_append_payload:get_token_start");
    const prefetchedTokenResult = await consumePrefetchedTurnstileToken(options, trace);
    const token = prefetchedTokenResult.status === "hit" ? prefetchedTokenResult.token : await getTurnstileToken(options);
    trace.mark("turnstile_append_payload:get_token_done", {hasToken: Boolean(token)});

    // The action is always sent so the backend can match it against the action Cloudflare embedded in the token -
    // this stops a token minted for one widget/endpoint from being replayed against another. It is included even
    // when Turnstile is disabled and no token is produced; only the token itself is conditional.
    if (!token) {
        trace.end("turnstile_append_payload:done", {hasToken: false});
        return {...payload, action: options.action};
    }

    trace.end("turnstile_append_payload:done", {hasToken: true});
    return {
        ...payload,
        turnstile_token: token,
        action: options.action
    };
};

export const createTurnstile = ({siteKey, createChallengeContainer, simulationStorageKey}: ICreateTurnstileOptions) => ({
    getTurnstileToken: (action: string) => getTurnstileToken({siteKey, action, createChallengeContainer, simulationStorageKey}),
    prefetchTurnstileToken: (action: string) => prefetchTurnstileToken({siteKey, action, createChallengeContainer, simulationStorageKey}),
    appendTurnstileToken: <T extends object>(payload: T, action: string) =>
        appendTurnstileToken(payload, {siteKey, action, createChallengeContainer, simulationStorageKey})
});
import {createPerformanceDebugTrace, type PerformanceDebugData} from "@pg-mono/performance-debug";

import {loadTurnstileScript} from "./load_turnstile_script";
import {createDefaultTurnstileChallengeContainer, CreateTurnstileChallengeContainer, TurnstileWidgetTheme} from "./turnstile_challenge_container";
import {createTurnstileValidationError} from "./turnstile_error";
import {
    getTurnstileSimulationMode,
    TURNSTILE_FORCE_CHALLENGE_SITE_KEY,
    TURNSTILE_SIMULATION_CHALLENGE_MODE,
    TURNSTILE_SIMULATION_FAIL_MODE
} from "./turnstile_simulation";

type TurnstileWidgetId = string;

type TurnstileRenderOptions = {
    sitekey: string;
    action: string;
    appearance: "always" | "interaction-only";
    execution: "execute";
    size: "flexible";
    theme: TurnstileWidgetTheme;
    callback: (token: string) => void;
    "before-interactive-callback": () => void;
    "after-interactive-callback": () => void;
    "error-callback": () => void;
    "expired-callback": () => void;
    "timeout-callback": () => void;
    "unsupported-callback": () => void;
};

declare global {
    interface Window {
        turnstile?: {
            render: (container: HTMLElement | string, options: TurnstileRenderOptions) => TurnstileWidgetId;
            execute: (widgetId: TurnstileWidgetId) => void;
            remove: (widgetId: TurnstileWidgetId) => void;
        };
    }
}

const TURNSTILE_TIMEOUT_MS = 30000;
const TURNSTILE_PREFETCH_TOKEN_MAX_AGE_MS = 240000;
const CLOUDFLARE_TURNSTILE_PERFORMANCE_GROUP = "cloudflare_turnstile";

interface IPrefetchedTurnstileToken {
    isResolved?: boolean;
    promise?: Promise<string | undefined>;
    resolvedAt?: number;
    token?: string;
}

type PrefetchedTokenResult = {status: "hit"; token?: string} | {status: "miss"};

const prefetchedTurnstileTokens = new Map<string, IPrefetchedTurnstileToken>();

interface IGetTurnstileTokenOptions {
    siteKey: string | null | undefined;
    action: string;
    createChallengeContainer?: CreateTurnstileChallengeContainer;
    simulationStorageKey?: string;
}

interface ICreateTurnstileOptions {
    siteKey: string | null | undefined;
    createChallengeContainer?: CreateTurnstileChallengeContainer;
    simulationStorageKey?: string;
}

const getTurnstileCacheKey = (options: IGetTurnstileTokenOptions) => {
    const simulationMode = getTurnstileSimulationMode(options.simulationStorageKey);
    const siteKey = getSiteKey({configuredSiteKey: options.siteKey, simulationMode});

    return `${siteKey ?? "disabled"}:${options.action}:${simulationMode ?? "off"}`;
};

const getSiteKey = ({
    configuredSiteKey,
    simulationMode
}: {
    configuredSiteKey: string | null | undefined;
    simulationMode: ReturnType<typeof getTurnstileSimulationMode>;
}) => {
    return simulationMode === null ? configuredSiteKey : TURNSTILE_FORCE_CHALLENGE_SITE_KEY;
};

const isPrefetchedTokenFresh = (entry?: IPrefetchedTurnstileToken) => {
    return Boolean(entry?.isResolved && entry.resolvedAt && Date.now() - entry.resolvedAt < TURNSTILE_PREFETCH_TOKEN_MAX_AGE_MS);
};

const createTurnstilePerformanceDebugTrace = (flow: string, initialData?: PerformanceDebugData) =>
    createPerformanceDebugTrace(flow, initialData, {group: CLOUDFLARE_TURNSTILE_PERFORMANCE_GROUP});

const consumePrefetchedTurnstileToken = async (
    options: IGetTurnstileTokenOptions,
    trace: ReturnType<typeof createPerformanceDebugTrace>
): Promise<PrefetchedTokenResult> => {
    const cacheKey = getTurnstileCacheKey(options);
    const cachedEntry = prefetchedTurnstileTokens.get(cacheKey);

    if (!cachedEntry) {
        trace.mark("turnstile_prefetch_cache:miss");
        return {status: "miss"};
    }

    prefetchedTurnstileTokens.delete(cacheKey);

    if (isPrefetchedTokenFresh(cachedEntry)) {
        trace.mark("turnstile_prefetch_cache:hit", {state: "resolved"});
        return {status: "hit", token: cachedEntry.token};
    }

    if (cachedEntry.promise) {
        trace.mark("turnstile_prefetch_cache:hit", {state: "pending"});
        const token = await cachedEntry.promise;
        trace.mark("turnstile_prefetch_cache:pending_resolved", {hasToken: Boolean(token)});

        return cachedEntry.isResolved ? {status: "hit", token} : {status: "miss"};
    }

    trace.mark("turnstile_prefetch_cache:expired");
    return {status: "miss"};
};

export const getTurnstileToken = async (options: IGetTurnstileTokenOptions): Promise<string | undefined> => {
    const {siteKey: configuredSiteKey, action, createChallengeContainer = createDefaultTurnstileChallengeContainer, simulationStorageKey} = options;

    const simulationMode = getTurnstileSimulationMode(simulationStorageKey);
    const siteKey = getSiteKey({configuredSiteKey, simulationMode});
    const trace = createTurnstilePerformanceDebugTrace("turnstile_token", {
        action,
        hasConfiguredSiteKey: Boolean(configuredSiteKey),
        isBrowser: typeof window !== "undefined",
        simulationMode: simulationMode ?? "off"
    });

    if (typeof window === "undefined" || !siteKey) {
        trace.end("turnstile_token:skipped", {
            reason: typeof window === "undefined" ? "not_browser" : "missing_site_key"
        });
        return undefined;
    }

    try {
        trace.mark("turnstile_script:load_start");
        await loadTurnstileScript(trace);
        trace.mark("turnstile_script:ready", {hasTurnstileApi: Boolean(window.turnstile)});

        if (!window.turnstile) {
            throw new Error("Turnstile is not available");
        }

        const token = await new Promise<string>((resolve, reject) => {
            const challengeContainer = createChallengeContainer({
                onDismiss: () => {
                    trace.mark("turnstile_challenge:dismissed");
                    rejectOnce("dismissed");
                }
            });

            let widgetId: TurnstileWidgetId | null = null;
            let isSettled = false;

            const cleanup = () => {
                window.clearTimeout(timeoutId);

                if (widgetId && window.turnstile) {
                    window.turnstile.remove(widgetId);
                    trace.mark("turnstile_widget:removed", {widgetId});
                }

                challengeContainer.remove();
                trace.mark("turnstile_challenge:container_removed");
            };

            const resolveOnce = (token: string) => {
                if (isSettled) {
                    return;
                }

                isSettled = true;
                trace.mark("turnstile_token:callback_success", {hasToken: Boolean(token)});
                cleanup();
                resolve(token);
            };

            const rejectOnce = (reason = "validation_error") => {
                if (isSettled) {
                    return;
                }

                isSettled = true;
                trace.mark("turnstile_token:rejected", {reason});
                cleanup();
                reject(createTurnstileValidationError());
            };

            const timeoutId = window.setTimeout(() => rejectOnce("local_timeout"), TURNSTILE_TIMEOUT_MS);

            if (!window.turnstile) {
                rejectOnce("missing_window_api");
                return;
            }

            if (simulationMode === TURNSTILE_SIMULATION_CHALLENGE_MODE) {
                trace.mark("turnstile_challenge:show_for_simulation");
                challengeContainer.show();
            }

            trace.mark("turnstile_widget:render_start");
            widgetId = window.turnstile.render(challengeContainer.widgetContainer, {
                sitekey: siteKey,
                action,
                appearance: simulationMode === TURNSTILE_SIMULATION_CHALLENGE_MODE ? "always" : "interaction-only",
                execution: "execute",
                size: "flexible",
                theme: challengeContainer.widgetTheme ?? "auto",
                callback: resolveOnce,
                "before-interactive-callback": () => {
                    trace.mark("turnstile_challenge:before_interactive");
                    challengeContainer.show();
                },
                "after-interactive-callback": () => trace.mark("turnstile_challenge:after_interactive"),
                "error-callback": () => rejectOnce("widget_error"),
                "expired-callback": () => rejectOnce("expired"),
                "timeout-callback": () => rejectOnce("widget_timeout"),
                "unsupported-callback": () => rejectOnce("unsupported")
            });

            trace.mark("turnstile_widget:render_done", {widgetId});
            window.turnstile.execute(widgetId);
            trace.mark("turnstile_widget:execute_done", {widgetId});
        });

        if (simulationMode === TURNSTILE_SIMULATION_FAIL_MODE) {
            trace.end("turnstile_token:simulation_failed");
            throw createTurnstileValidationError();
        }

        if (simulationMode === TURNSTILE_SIMULATION_CHALLENGE_MODE) {
            trace.end("turnstile_token:simulation_challenge_resolved");
            return undefined;
        }

        trace.end("turnstile_token:resolved", {hasToken: Boolean(token)});
        return token;
    } catch (error) {
        trace.end("turnstile_token:failed", {
            isAppError: typeof error === "object" && error !== null && "appError" in error
        });

        if (typeof error === "object" && error !== null && "appError" in error) {
            throw error;
        }

        throw createTurnstileValidationError();
    }
};

export const prefetchTurnstileToken = (options: IGetTurnstileTokenOptions): void => {
    const cacheKey = getTurnstileCacheKey(options);
    const trace = createTurnstilePerformanceDebugTrace("turnstile_prefetch_token", {
        action: options.action
    });
    const cachedEntry = prefetchedTurnstileTokens.get(cacheKey);

    if (cachedEntry) {
        if (cachedEntry.promise || isPrefetchedTokenFresh(cachedEntry)) {
            trace.end("turnstile_prefetch:skipped", {reason: cachedEntry.promise ? "pending" : "fresh_token"});
            return;
        }
    }

    const nextEntry: IPrefetchedTurnstileToken = {};
    const promise = getTurnstileToken(options)
        .then((token) => {
            nextEntry.isResolved = true;
            nextEntry.promise = undefined;
            nextEntry.resolvedAt = Date.now();
            nextEntry.token = token;
            trace.end("turnstile_prefetch:done", {hasToken: Boolean(token)});

            return token;
        })
        .catch(() => {
            prefetchedTurnstileTokens.delete(cacheKey);
            trace.end("turnstile_prefetch:error");

            return undefined;
        });

    nextEntry.promise = promise;
    prefetchedTurnstileTokens.set(cacheKey, nextEntry);
};

export const appendTurnstileToken = async <T extends object>(
    payload: T,
    options: IGetTurnstileTokenOptions
): Promise<T & {turnstile_token?: string; action: string}> => {
    const trace = createTurnstilePerformanceDebugTrace("turnstile_append_payload", {
        action: options.action
    });
    trace.mark("turnstile_append_payload:get_token_start");
    const prefetchedTokenResult = await consumePrefetchedTurnstileToken(options, trace);
    const token = prefetchedTokenResult.status === "hit" ? prefetchedTokenResult.token : await getTurnstileToken(options);
    trace.mark("turnstile_append_payload:get_token_done", {hasToken: Boolean(token)});

    // The action is always sent so the backend can match it against the action Cloudflare embedded in the token -
    // this stops a token minted for one widget/endpoint from being replayed against another. It is included even
    // when Turnstile is disabled and no token is produced; only the token itself is conditional.
    if (!token) {
        trace.end("turnstile_append_payload:done", {hasToken: false});
        return {...payload, action: options.action};
    }

    trace.end("turnstile_append_payload:done", {hasToken: true});
    return {
        ...payload,
        turnstile_token: token,
        action: options.action
    };
};

export const createTurnstile = ({siteKey, createChallengeContainer, simulationStorageKey}: ICreateTurnstileOptions) => ({
    getTurnstileToken: (action: string) => getTurnstileToken({siteKey, action, createChallengeContainer, simulationStorageKey}),
    prefetchTurnstileToken: (action: string) => prefetchTurnstileToken({siteKey, action, createChallengeContainer, simulationStorageKey}),
    appendTurnstileToken: <T extends object>(payload: T, action: string) =>
        appendTurnstileToken(payload, {siteKey, action, createChallengeContainer, simulationStorageKey})
});
